import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { LoadSuccessParams } from 'ag-grid-community';

import {
  EXPORT_TEMPLATE_GET_GET_EXPORT_TEMPLATE_HTML_OPTIONS,
  EXPORT_TEMPLATE_GET_GET_TEMPLATE,
  EXPORT_TEMPLATE_GET_LIST_TEMPLATES,
  EXPORT_TEMPLATE_POST_ADD_TEMPLATE,
  EXPORT_TEMPLATE_POST_EDIT_TEMPLATE,
  EXPORT_TEMPLATE_POST_LIST_TEMPLATES,
  EXPORT_TEMPLATE_POST_TOGGLE_TEMPLATE_STATUS,
} from '../endpoints/export-template.endpoints';

import { toQueryParams } from '../api-utils';

import { saveTemplate } from '../serialize/export-template.serialize';

import { ExportTemplate } from 'src/app/core/models/core.models';
import { FormOption } from 'src/app/core/services/forms.service';

import { Observable } from 'rxjs';

/**
 * Values the export-template editor produces; also the multipart request shape
 * (see export-template.serialize.ts). `file_path` is the uploaded file itself.
 */
export interface ExportTemplateFormValue {
  output_format_id: number;
  name: string;
  file_path: File | null;
  html_template_file_path: string | null;
  language_id: number;
  is_default: boolean | null;
  companies: string[] | null;
}

/** add_template returns the template bare; edit_template wraps it. */
export interface EditExportTemplateResponse {
  success: boolean;
  error?: string;
  export_template?: ExportTemplate;
}

@Injectable({
  providedIn: 'root',
})
export class ExportTemplateApiService {
  private readonly http = inject(HttpClient);

  /** List export templates for current user's company. */
  listTemplates(
    companyId: string,
    outputFormatId: number,
  ): Observable<ExportTemplate[]> {
    return this.http.get<ExportTemplate[]>(EXPORT_TEMPLATE_GET_LIST_TEMPLATES, {
      params: { company_id: companyId, output_format_id: outputFormatId },
    });
  }

  getTemplate(templateId: string): Observable<ExportTemplate> {
    return this.http.get<ExportTemplate>(EXPORT_TEMPLATE_GET_GET_TEMPLATE, {
      params: { template_id: templateId },
    });
  }

  getExportTemplatesAgGridModel(
    gridParams: any,
  ): Observable<LoadSuccessParams> {
    const httpParams = toQueryParams({ output: 'table' });

    return this.http.post<LoadSuccessParams>(
      EXPORT_TEMPLATE_POST_LIST_TEMPLATES,
      gridParams,
      {
        params: httpParams,
      },
    );
  }

  getCompanyExportTemplateHtmlOptions(): Observable<FormOption[]> {
    return this.http.get<FormOption[]>(
      EXPORT_TEMPLATE_GET_GET_EXPORT_TEMPLATE_HTML_OPTIONS,
      { params: {} },
    );
  }

  toggleExportTemplateStatus(
    id: string,
    isEnabled: boolean,
  ): Observable<boolean> {
    const body = {
      id: id,
      is_enabled: isEnabled,
    };

    return this.http.post<any>(EXPORT_TEMPLATE_POST_TOGGLE_TEMPLATE_STATUS, body);
  }

  addTemplate(template: ExportTemplateFormValue): Observable<ExportTemplate> {
    return this.http.post<ExportTemplate>(
      EXPORT_TEMPLATE_POST_ADD_TEMPLATE,
      saveTemplate.toMultipartFormData(template),
    );
  }

  editTemplate(
    id: string | number,
    template: ExportTemplateFormValue,
  ): Observable<EditExportTemplateResponse> {
    return this.http.post<EditExportTemplateResponse>(
      EXPORT_TEMPLATE_POST_EDIT_TEMPLATE,
      saveTemplate.toMultipartFormData(template, id),
    );
  }
}
